// frontend/src/pages/SubscriptionPage.tsx
import React, { useState, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useSearchParams } from 'react-router-dom';
import { IconCheck, IconMinus, IconCrown, IconWarning } from '../icons';
import { startSubscriptionCheckout, redirectToCustomerPortal, CheckoutStartError, type CheckoutFailureReason } from '../api/stripeApi';
import { httpsCallable } from 'firebase/functions';
import { functions } from '../firebaseConfig';
import logger from '../utils/logger';
import { mapErrorToUserMessage } from '../utils/userErrorMessage';
import { formatDate } from '../utils/formatters';
import { useAuth } from '../context/AuthContext';
import {
  parseNudgeRef,
  parseNudgeTier,
  NUDGE_REF_PARAM,
  NUDGE_TIER_PARAM,
  type NudgePromotionId,
} from '../config/nudgePromotions';
import {
  TIER_FEATURE_ROWS,
  RECOMMENDED_TIER,
  ULTRA_TIER_FLAG,
  ULTRA_PRICE_ID_MONTHLY,
  ULTRA_PRICE_ID_YEARLY,
  isUltraCheckoutReady,
  type TierId,
} from '../config/tiers';
import { resolveTier } from '../config/entitlements';

// Definiere die Price IDs - diese werden weiterhin für den Checkout benötigt
// const MONTHLY_PRICE_ID = 'price_1RD2H5RhesKAfWh3tLbWKJGJ'; // Entfernt
// const YEARLY_PRICE_ID = 'price_1RD2HhRhesKAfWh3bNIxiB7n'; // Entfernt

// Dupliziertes Interface aus der Cloud Function für Klarheit
interface RecurringInfo {
  interval: string | null;
  interval_count: number | null;
}

interface PriceDetail {
  id: string;
  unitAmount: number | null;
  currency: string;
  recurring: RecurringInfo | null; // Angepasster Typ
  productName: string;
  productDescription: string | null;
}

interface FetchedPrices {
  monthly?: PriceDetail;
  yearly?: PriceDetail;
  error?: string;
}

/** Abrechnungszeitraum-Auswahl der 3-Spalten-Tabelle (Ultra-Tabelle, W-UX-G3). */
type BillingPeriod = 'monthly' | 'yearly';

/**
 * `formatDate` gibt bei fehlendem/unparsebarem Input dieses Anzeige-Zeichen
 * zurück (Konvention `utils/formatters.ts`) — hier lokal gespiegelt, um zu
 * entscheiden, ob der Dunning-Hinweis die Datumsvariante zeigen kann.
 */
const NO_DATE_PLACEHOLDER = '—';

/**
 * Checkout-Fehlerbanner (architecture-past-due-reactivation.md §7.2): zeigt die
 * kuratierte Fehlermeldung und — nur bei `active_subscription`/`payment_pending`
 * — einen zusätzlichen Button ins Kundenportal. Wird von BEIDEN Render-Zweigen
 * (Legacy 2-Kachel-Ansicht und `TierComparisonTable`) geteilt, damit das
 * Fehler-Mapping an genau einer Stelle lebt.
 */
const CheckoutErrorBanner: React.FC<{
  error: string | null;
  reason: CheckoutFailureReason | null;
  onOpenPortal: () => void;
}> = ({ error, reason, onOpenPortal }) => {
  const { t } = useTranslation('subscription');
  if (!error) return null;
  const showPortalButton = reason === 'active_subscription' || reason === 'payment_pending';
  return (
    <div className="bg-[var(--tone-danger-bg)] border border-[var(--tone-danger-border)] text-[var(--tone-danger-fg)] px-4 py-3 rounded-lg mb-4">
      <p>{error}</p>
      {showPortalButton && (
        <button
          type="button"
          onClick={onOpenPortal}
          className="mt-2 text-sm font-medium underline hover:no-underline"
        >
          {t('checkoutErrors.openPortal')}
        </button>
      )}
    </div>
  );
};

/**
 * Dunning-Hinweis (Rev. 2 §7.2, „fast gratis"): sichtbar, solange der Nutzer
 * noch Premium hat, aber die letzte Abbuchung fehlgeschlagen ist
 * (`isPremium && sellerData.dunningActive === true`). Ruft über `onPayNow`
 * DENSELBEN `handleCheckout`-Pfad wie die normalen Tarif-Buttons auf — im
 * Bezahlzweig (Server `mode=on`) landet der Klick auf der Stripe-Bezahlseite
 * der offenen Rechnung, sonst zeigt der bestehende `payment_pending`-Fehlertext
 * mit Portal-Button denselben Weg (§5.4/§6.2). Kein FE-Flag, kein Polling.
 */
const DunningHint: React.FC<{
  gracePeriodEnd: { toDate: () => Date } | Date | undefined;
  loading: boolean;
  onPayNow: () => void;
}> = ({ gracePeriodEnd, loading, onPayNow }) => {
  const { t } = useTranslation('subscription');
  const formattedDeadline = formatDate(gracePeriodEnd as any);
  const hasDeadline = formattedDeadline !== NO_DATE_PLACEHOLDER;
  return (
    <div className="mb-6 flex items-start gap-3 rounded-md border border-[var(--tone-warning-border)] bg-[var(--tone-warning-bg)] p-4">
      <IconWarning className="mt-0.5 shrink-0 text-[var(--tone-warning-fg)]" />
      <div className="flex-1">
        <p className="text-sm text-[var(--tone-warning-fg)]">
          {hasDeadline ? t('dunningHint.body', { date: formattedDeadline }) : t('dunningHint.bodyNoDate')}
        </p>
        <button
          type="button"
          onClick={onPayNow}
          disabled={loading}
          className="mt-3 px-4 py-2 rounded-md text-sm font-semibold bg-[var(--tone-warning-fg)] text-[var(--tone-warning-bg)] disabled:opacity-50 disabled:cursor-not-allowed transition"
        >
          {loading ? t('tierTable.cta.loading') : t('dunningHint.payNow')}
        </button>
      </div>
    </div>
  );
};

/**
 * `Free / Premium / Ultra`-Vergleichstabelle, sichtbar nur hinter `REACT_APP_ULTRA_TIER_ENABLED`
 * (fail-closed). Zeigt die Merkmals-Matrix aus `config/tiers.ts`; Premium-Preise kommen wie im
 * Legacy-Pfad live von Stripe, Ultra-Preise werden bewusst NICHT angezeigt/gekauft, solange
 * `isUltraCheckoutReady()` false ist (⛔ cfo-Gate).
 */
const TierComparisonTable: React.FC<{
  prices: FetchedPrices;
  error: string | null;
  errorReason: CheckoutFailureReason | null;
  onOpenPortal: () => void;
  loadingCheckoutMonthly: boolean;
  loadingCheckoutYearly: boolean;
  loadingCheckoutUltra: boolean;
  handleCheckout: (priceId: string | undefined, setLoading: React.Dispatch<React.SetStateAction<boolean>>) => void;
  setLoadingCheckoutMonthly: React.Dispatch<React.SetStateAction<boolean>>;
  setLoadingCheckoutYearly: React.Dispatch<React.SetStateAction<boolean>>;
  setLoadingCheckoutUltra: React.Dispatch<React.SetStateAction<boolean>>;
  formatPrice: (amount: number | null, currency: string) => string;
}> = ({
  prices,
  error,
  errorReason,
  onOpenPortal,
  loadingCheckoutMonthly,
  loadingCheckoutYearly,
  loadingCheckoutUltra,
  handleCheckout,
  setLoadingCheckoutMonthly,
  setLoadingCheckoutYearly,
  setLoadingCheckoutUltra,
  formatPrice,
}) => {
  const { t } = useTranslation('subscription');
  const { sellerData, isPremium } = useAuth();
  const [billingPeriod, setBillingPeriod] = useState<BillingPeriod>('yearly');
  const ultraCheckoutReady = isUltraCheckoutReady();

  // ─── D-1: Ziel-Tarif aus `?tier=` hervorheben ────────────────────────────
  // Wer auf „Jetzt auf Ultra upgraden" klickt, muss hier SICHTBAR bei Ultra
  // landen — vorher trug der CTA zwar ein `?tier=ultra` im (toten) `ctaTo`-Prop,
  // die Abo-Seite las es aber nie aus, und auf schmalen Viewports steht die
  // Ultra-Spalte als DRITTE Karte unterhalb des Folds. `parseNudgeTier` ist
  // fail-closed: unbekannter/fehlender Wert ⇒ `undefined` ⇒ keine Hervorhebung
  // (kein Fehler, nur kein Fokus). Einmalig beim Mount eingefroren, damit ein
  // späterer Wechsel des Abrechnungszeitraums nicht erneut scrollt.
  const [searchParams] = useSearchParams();
  const [highlightTier] = useState<TierId | undefined>(() =>
    parseNudgeTier(searchParams.get(NUDGE_TIER_PARAM)),
  );
  const highlightCardRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    const el = highlightCardRef.current;
    // jsdom implementiert `scrollIntoView`/`matchMedia` nicht — beide defensiv
    // prüfen, sonst wirft der Effekt in jedem Test, der diese Seite rendert.
    if (!el || typeof el.scrollIntoView !== 'function') {
      return;
    }
    const prefersReducedMotion =
      typeof window.matchMedia === 'function' &&
      window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    try {
      el.scrollIntoView({
        behavior: prefersReducedMotion ? 'auto' : 'smooth',
        block: 'nearest',
        inline: 'center',
      });
    } catch {
      // Ältere Browser ohne Options-Objekt: einfacher Aufruf reicht.
      el.scrollIntoView();
    }
  }, [highlightTier]);

  /**
   * Ring in der Tarif-eigenen Tonfarbe für die per `?tier=` angesteuerte Karte.
   * Bewusst NUR ein Ring und kein zusätzliches Badge: Premium trägt bereits
   * „Empfohlen", Ultra bereits „Neu" — eine dritte Pille wäre Rauschen.
   */
  const HIGHLIGHT_RING: Record<TierId, string> = {
    free: 'ring-2 ring-[var(--tone-info-border)]',
    premium: 'ring-2 ring-indigo-500',
    ultra: 'ring-2 ring-[var(--tone-warning-border)]',
  };
  const highlightClasses = (tier: TierId): string =>
    highlightTier === tier ? ` ${HIGHLIGHT_RING[tier]}` : '';
  const highlightRefFor = (tier: TierId) => (highlightTier === tier ? highlightCardRef : undefined);
  // Eine Tier-Wahrheit (§3.2) — ersetzt die separate `isPremium`-Ableitung für
  // die „aktueller Plan"-Badges. `resolveTier` ist fail-closed identisch zur
  // functions-Version `resolveEffectivePlan` (byte-semantisch, §2.1/§7).
  const currentTier = resolveTier(sellerData);

  const activePremiumPrice = billingPeriod === 'yearly' ? prices.yearly : prices.monthly;
  const premiumPriceLabel = activePremiumPrice
    ? `${formatPrice(activePremiumPrice.unitAmount, activePremiumPrice.currency)} / ${
        billingPeriod === 'yearly' ? t('tierTable.perYear') : t('tierTable.perMonth')
      }`
    : t('tierTable.priceUnavailable');

  const handlePremiumCheckout = () => {
    const targetPrice = billingPeriod === 'yearly' ? prices.yearly : prices.monthly;
    const setLoading = billingPeriod === 'yearly' ? setLoadingCheckoutYearly : setLoadingCheckoutMonthly;
    handleCheckout(targetPrice?.id, setLoading);
  };
  const premiumLoading = billingPeriod === 'yearly' ? loadingCheckoutYearly : loadingCheckoutMonthly;

  // M-U3 (Ultra-GA Welle 1): Ultra-Checkout-Verdrahtung analog Premium. Ein-Kohorten-
  // Modell — die period-abhängige Standard-Preis-ID (ENV, build-time) wird direkt an
  // `handleCheckout` gereicht. Fehlt die passende ID (unset), fängt `handleCheckout`
  // das `undefined` fail-closed ab (Fehlermeldung, kein Stripe-Aufruf). Der Button ist
  // ohnehin nur klickbar, wenn `ultraCheckoutReady` (Flag ∧ beide Standard-IDs) gilt.
  const handleUltraCheckout = () => {
    const priceId = billingPeriod === 'yearly' ? ULTRA_PRICE_ID_YEARLY : ULTRA_PRICE_ID_MONTHLY;
    handleCheckout(priceId, setLoadingCheckoutUltra);
  };

  // Eine Tier-Wahrheit (§3.2/§3.7): alle drei Vergleiche laufen konsistent über
  // `resolveTier(sellerData)` statt über den separaten `isPremium`-Boolean.
  // Ultra wird jetzt korrekt erkannt, sobald `sellerData.plan==='ultra'` gesetzt
  // ist (Webhook-seitig, §2.2) — live noch niemand, da Ultra-Preis-IDs unset
  // sind (PR-T1 Inert-Kriterium), aber kein hartkodiertes `false` mehr.
  const currentPlanOf = (tier: TierId): boolean => currentTier === tier;

  const tierMeta: Record<TierId, { name: string }> = {
    free: { name: t('tierTable.tiers.free.name') },
    premium: { name: t('tierTable.tiers.premium.name') },
    ultra: { name: t('tierTable.tiers.ultra.name') },
  };

  return (
    <div className="container mx-auto p-4">
      <h1 className="text-2xl font-bold mb-2 text-gray-900">{t('tierTable.heading')}</h1>
      <p className="text-gray-600 mb-6">{t('tierTable.subheading')}</p>

      <CheckoutErrorBanner error={error} reason={errorReason} onOpenPortal={onOpenPortal} />

      {isPremium && sellerData?.dunningActive === true && (
        <DunningHint
          gracePeriodEnd={sellerData.gracePeriodEnd}
          loading={loadingCheckoutMonthly}
          onPayNow={() => handleCheckout(prices.monthly?.id, setLoadingCheckoutMonthly)}
        />
      )}

      <div className="flex justify-center mb-8">
        <div className="inline-flex rounded-lg border border-hairline bg-surface-raised p-1">
          <button
            type="button"
            onClick={() => setBillingPeriod('monthly')}
            className={`px-4 py-2 rounded-md text-sm font-medium transition ${
              billingPeriod === 'monthly' ? 'bg-indigo-600 text-[#0C1322]' : 'text-secondary'
            }`}
          >
            {t('tierTable.billingToggle.monthly')}
          </button>
          <button
            type="button"
            onClick={() => setBillingPeriod('yearly')}
            className={`px-4 py-2 rounded-md text-sm font-medium transition ${
              billingPeriod === 'yearly' ? 'bg-indigo-600 text-[#0C1322]' : 'text-secondary'
            }`}
          >
            {t('tierTable.billingToggle.yearly')}
          </button>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        {/* Free */}
        <div
          ref={highlightRefFor('free')}
          data-tier-card="free"
          data-tier-highlighted={highlightTier === 'free' ? 'true' : undefined}
          className={`bg-surface-raised shadow-lg rounded-xl border border-hairline p-6 flex flex-col${highlightClasses('free')}`}
        >
          {currentPlanOf('free') && (
            <div className="bg-surface-inset text-secondary text-xs font-medium px-3 py-1 rounded-full w-fit mb-3">
              {t('tierTable.currentPlanBadge')}
            </div>
          )}
          <h2 className="text-xl font-semibold mb-2 text-primary">{tierMeta.free.name}</h2>
          <p className="text-xl font-bold text-primary mb-6">{t('tierTable.tiers.free.priceLabel')}</p>
          <ul className="space-y-3 mb-6 flex-1">
            {TIER_FEATURE_ROWS.map((row) => (
              <FeatureRow key={row.labelKey} included={row.included.free} label={t(`tierTable.features.${row.labelKey}`)} />
            ))}
          </ul>
          <button
            type="button"
            disabled
            className="w-full px-4 py-3 rounded-md font-semibold bg-surface-inset text-muted cursor-not-allowed"
          >
            {t('tierTable.cta.free')}
          </button>
        </div>

        {/* Premium */}
        <div
          ref={highlightRefFor('premium')}
          data-tier-card="premium"
          data-tier-highlighted={highlightTier === 'premium' ? 'true' : undefined}
          className={`bg-surface-raised shadow-lg rounded-xl border p-6 flex flex-col ${
            RECOMMENDED_TIER === 'premium' ? 'border-indigo-500 ring-1 ring-indigo-500' : 'border-hairline'
          }${highlightClasses('premium')}`}
        >
          {RECOMMENDED_TIER === 'premium' && (
            <div className="bg-indigo-600 text-[#0C1322] text-sm font-medium px-3 py-1 rounded-full w-fit mb-3">
              {t('tierTable.recommendedBadge')}
            </div>
          )}
          {currentPlanOf('premium') && (
            <div className="bg-surface-inset text-secondary text-xs font-medium px-3 py-1 rounded-full w-fit mb-3">
              {t('tierTable.currentPlanBadge')}
            </div>
          )}
          <h2 className="text-xl font-semibold mb-2 text-primary">{tierMeta.premium.name}</h2>
          {/* ⛔ jurist: Preisdarstellung PAngV (Brutto, Zeitraum, Kündigungshinweis) vor Flag-Flip prüfen */}
          <p className="text-xl font-bold text-accent-ink mb-6">{premiumPriceLabel}</p>
          <ul className="space-y-3 mb-6 flex-1">
            {TIER_FEATURE_ROWS.map((row) => (
              <FeatureRow
                key={row.labelKey}
                included={row.included.premium}
                label={t(`tierTable.features.${row.labelKey}`)}
              />
            ))}
          </ul>
          <button
            type="button"
            onClick={handlePremiumCheckout}
            disabled={premiumLoading || !activePremiumPrice?.id}
            className={`w-full px-4 py-3 rounded-md font-semibold ${
              premiumLoading ? 'bg-surface-hover text-primary' : 'bg-indigo-600 hover:bg-indigo-700 text-[#0C1322]'
            } disabled:opacity-50 disabled:cursor-not-allowed transition`}
          >
            {premiumLoading
              ? t('tierTable.cta.loading')
              : billingPeriod === 'yearly'
                ? t('tierTable.cta.premiumYearly')
                : t('tierTable.cta.premiumMonthly')}
          </button>
        </div>

        {/* Ultra */}
        <div
          ref={highlightRefFor('ultra')}
          data-tier-card="ultra"
          data-tier-highlighted={highlightTier === 'ultra' ? 'true' : undefined}
          className={`bg-surface-raised shadow-lg rounded-xl border border-amber-600/50 p-6 flex flex-col${highlightClasses('ultra')}`}
        >
          {currentPlanOf('ultra') && (
            <div className="bg-surface-inset text-secondary text-xs font-medium px-3 py-1 rounded-full w-fit mb-3">
              {t('tierTable.currentPlanBadge')}
            </div>
          )}
          <div className="flex items-center gap-2 mb-3">
            <IconCrown className="text-[var(--tone-warning-fg)]" />
            <span className="bg-[var(--tone-warning-bg)] text-[var(--tone-warning-fg)] text-xs font-medium px-3 py-1 rounded-full w-fit">
              {t('tierTable.tiers.ultra.newBadge')}
            </span>
          </div>
          <h2 className="text-xl font-semibold mb-2 text-primary">{tierMeta.ultra.name}</h2>
          {/* M-U3: Preisdarstellung erst bei kaufbarem Ultra (`ultraCheckoutReady`);
              solange nicht kaufbar, bleibt der bestehende „In Kürze"-Platzhalter.
              ⛔ jurist (M-U6): PAngV/UWG-Endabnahme der Preis-/Streichpreis-Copy. */}
          {ultraCheckoutReady ? (
            <div className="mb-6">
              <p className="text-xl font-bold text-[var(--tone-warning-fg)]">
                {billingPeriod === 'yearly'
                  ? t('tierTable.tiers.ultra.priceYearly')
                  : t('tierTable.tiers.ultra.priceMonthly')}
              </p>
              <span className="text-sm font-normal text-muted block">
                {billingPeriod === 'yearly'
                  ? t('tierTable.tiers.ultra.priceYearlyMonthlyEquivalent')
                  : t('tierTable.tiers.ultra.introOffer')}
              </span>
            </div>
          ) : (
            <p className="text-xl font-bold text-[var(--tone-warning-fg)] mb-6">{t('tierTable.tiers.ultra.comingSoon')}</p>
          )}
          <ul className="space-y-3 mb-6 flex-1">
            {TIER_FEATURE_ROWS.map((row) => (
              <FeatureRow
                key={row.labelKey}
                included={row.included.ultra}
                label={t(`tierTable.features.${row.labelKey}`)}
                comingSoon={row.comingSoon}
                comingSoonLabel={t('tierTable.badges.comingSoon')}
              />
            ))}
          </ul>
          <button
            type="button"
            onClick={handleUltraCheckout}
            disabled={!ultraCheckoutReady || loadingCheckoutUltra}
            title={ultraCheckoutReady ? undefined : t('tierTable.cta.ultraComingSoonHint')}
            className="w-full px-4 py-3 rounded-md font-semibold bg-[var(--tone-warning-bg)] text-[var(--tone-warning-fg)] disabled:opacity-50 disabled:cursor-not-allowed transition"
          >
            {loadingCheckoutUltra
              ? t('tierTable.cta.loading')
              : ultraCheckoutReady
                ? billingPeriod === 'yearly'
                  ? t('tierTable.cta.ultraYearly')
                  : t('tierTable.cta.ultraMonthly')
                : t('tierTable.cta.ultraComingSoon')}
          </button>
        </div>
      </div>

      <p className="text-xs text-faint mt-6 text-center">{t('tierTable.legalFootnotePending')}</p>
    </div>
  );
};

/**
 * Eine Merkmalszeile in der Vergleichstabelle (Häkchen oder Minus + Label).
 * M-U5: Ist das Merkmal in dieser Spalte enthalten UND als `comingSoon` markiert,
 * wird ein gedämpftes „In Kürze"-Badge neben dem Label gerendert — das Häkchen
 * bleibt (Verkaufsargument), die Verfügbarkeit ist aber ehrlich gekennzeichnet.
 */
const FeatureRow: React.FC<{
  included: boolean;
  label: string;
  comingSoon?: boolean;
  comingSoonLabel?: string;
}> = ({ included, label, comingSoon, comingSoonLabel }) => (
  <li className="flex items-start gap-2 text-sm">
    {included ? (
      <IconCheck className="text-accent-ink mt-0.5 flex-shrink-0" size={14} />
    ) : (
      <IconMinus className="text-gray-600 mt-0.5 flex-shrink-0" size={14} />
    )}
    <span className={included ? 'text-secondary' : 'text-faint'}>{label}</span>
    {included && comingSoon && comingSoonLabel && (
      <span className="ml-1 whitespace-nowrap text-[10px] font-medium px-1.5 py-0.5 rounded-full bg-[var(--tone-warning-bg)] text-[var(--tone-warning-fg)]">
        {comingSoonLabel}
      </span>
    )}
  </li>
);

const SubscriptionPage: React.FC = () => {
  const { t } = useTranslation('subscription');
  const { isPremium, sellerData } = useAuth();
  const [loadingCheckoutMonthly, setLoadingCheckoutMonthly] = useState(false);
  const [loadingCheckoutYearly, setLoadingCheckoutYearly] = useState(false);
  // M-U3: eigener Loading-State für den Ultra-Checkout — NICHT die geteilten
  // monthly/yearly-States, sonst zeigte der Premium-Button beim Ultra-Klick „lädt".
  const [loadingCheckoutUltra, setLoadingCheckoutUltra] = useState(false);
  const [prices, setPrices] = useState<FetchedPrices>({});
  const [loadingPrices, setLoadingPrices] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  // Zahlungsausfall-Fehler-Mapping (§7.2): trägt den fachlichen Grund eines
  // `CheckoutStartError`, damit der Portal-Button gezielt nur bei
  // `active_subscription`/`payment_pending` erscheint. `null` bei jedem
  // anderen Fehlerpfad (IST-Fallback über `mapErrorToUserMessage`).
  const [checkoutErrorReason, setCheckoutErrorReason] = useState<CheckoutFailureReason | null>(null);
  const [searchParams] = useSearchParams();
  // Nudge-Attribution (M-N4, architecture-nudge.md §7.5): die auslösende
  // Promotion-ID EINMALIG beim Mount aus `?ref=` lesen und fail-closed via
  // `parseNudgeRef` validieren (unbekannt/leer ⇒ undefined, nie Freitext). Der
  // Lazy-Initializer friert den Wert im State ein, sodass er über die
  // Preis-Lade-Phase bis zum späteren Checkout-Klick stabil erhalten bleibt.
  const [nudgeRef] = useState<NudgePromotionId | undefined>(() =>
    parseNudgeRef(searchParams.get(NUDGE_REF_PARAM))
  );

  useEffect(() => {
    const fetchPrices = async () => {
      setLoadingPrices(true);
      setError(null);
      try {
        const getPricesFunction = httpsCallable<undefined, FetchedPrices>(functions, 'getStripeProductPrices');
        const result = await getPricesFunction();
        if (result.data.error) {
          logger.error("Error fetching prices from CF: " + result.data.error, "price-fetch-error");
          setError("Preisdetails konnten nicht geladen werden: " + result.data.error);
        } else {
          setPrices(result.data);
          logger.info("CLIENT: Successfully fetched prices from getStripeProductPrices:", result.data);
        }
      } catch (err: any) {
        logger.error(err, "price-fetch-function-call-error");
        setError(mapErrorToUserMessage(err, t, { fallbackKey: 'errors.payment.checkoutFailed' }));
      }
      setLoadingPrices(false);
    };

    fetchPrices();
  }, []);

  const handleCheckout = async (priceId: string | undefined, setLoading: React.Dispatch<React.SetStateAction<boolean>>) => {
    console.log("CLIENT: handleCheckout CALLED WITH priceId:", priceId);
    setError(null);
    setCheckoutErrorReason(null);
    setLoading(true);
    if (!priceId) {
      logger.error("Checkout attempt with undefined priceId", "checkout-error");
      setError("Preis-ID ist nicht verfügbar. Das Paket kann nicht ausgewählt werden.");
      console.log("CLIENT: handleCheckout STOPPED because priceId is undefined");
      setLoading(false);
      return;
    }
    try {
      console.log("CLIENT: Calling startSubscriptionCheckout with priceId:", priceId);
      // Revenue-Metadaten aus dem geladenen Stripe-Preis ableiten: unitAmount
      // (Cents, integer) → value (Euro, /100, 2-Dezimal), currency → UPPERCASE
      // (ISO-4217). Ohne Treffer/Betrag bleibt meta undefined → begin_checkout
      // feuert GA4-valide ohne value/currency.
      const matchedPrice = [prices.monthly, prices.yearly].find((p) => p?.id === priceId);
      const revenueMeta =
        matchedPrice && matchedPrice.unitAmount != null
          ? { value: matchedPrice.unitAmount / 100, currency: matchedPrice.currency?.toUpperCase() }
          : undefined;
      // Nudge-Attribution (M-N4): den beim Mount aus `?ref=` gelesenen `nudgeRef`
      // additiv in den bestehenden #214-meta-Weg mergen — nur wenn vorhanden, sonst
      // bleibt der Checkout-Aufruf byte-identisch zum #214-Bestand (kein leerer Wert).
      const meta =
        nudgeRef !== undefined ? { ...(revenueMeta ?? {}), nudgeRef } : revenueMeta;
      const checkoutUrl = await startSubscriptionCheckout(priceId, meta);
      window.location.assign(checkoutUrl);
    } catch (err: any) {
      logger.error(err, "checkout-fehler");
      // §7.2: `CheckoutStartError` mit bekanntem `reason` bekommt eine kuratierte,
      // fachliche Meldung (`checkoutErrors.<reason>`); `reason === "unknown"` oder
      // ein anderer Fehlertyp fällt auf das bestehende generische Mapping zurück.
      if (err instanceof CheckoutStartError && err.reason !== 'unknown') {
        setError(t(`checkoutErrors.${err.reason}`));
        setCheckoutErrorReason(err.reason);
      } else {
        setError(mapErrorToUserMessage(err, t, { fallbackKey: 'errors.payment.checkoutFailed' }));
        setCheckoutErrorReason(null);
      }
      setLoading(false);
    }
  };

  /**
   * Öffnet das Stripe-Kundenportal aus dem Checkout-Fehlerbanner heraus
   * (`checkoutErrors.openPortal`, §7.2) — derselbe Weg, den `AccountPage`
   * bereits über `redirectToCustomerPortal` nutzt.
   */
  const handleOpenPortal = async () => {
    try {
      const portalUrl = await redirectToCustomerPortal();
      window.location.assign(portalUrl);
    } catch (err: any) {
      logger.error(err, "checkout-portal-fehler");
      setError(mapErrorToUserMessage(err, t, { fallbackKey: 'errors.payment.checkoutFailed' }));
    }
  };

  const formatPrice = (amount: number | null, currency: string): string => {
    if (amount === null) return 'N/A';
    return new Intl.NumberFormat('de-DE', { style: 'currency', currency: currency.toUpperCase() }).format(amount / 100);
  };

  if (loadingPrices) {
    return (
      <div className="container mx-auto p-4 text-center">
        <p className="text-xl text-gray-700">Lade Abonnement-Optionen...</p>
        {/* Optional: Add a spinner icon here */}
      </div>
    );
  }

  // Flag off (Default): heutige 2-Kachel-Ansicht bleibt UNVERÄNDERT (RTL-Byte-Parität, M-UX-G3-Akzeptanzkriterium).
  if (!ULTRA_TIER_FLAG) {
    return (
      <div className="container mx-auto p-4">
        <h1 className="text-2xl font-bold mb-4 text-gray-900">Wähle dein Premium-Abonnement</h1>

        <CheckoutErrorBanner error={error} reason={checkoutErrorReason} onOpenPortal={handleOpenPortal} />

        {isPremium && sellerData?.dunningActive === true && (
          <DunningHint
            gracePeriodEnd={sellerData.gracePeriodEnd}
            loading={loadingCheckoutMonthly}
            onPayNow={() => handleCheckout(prices.monthly?.id, setLoadingCheckoutMonthly)}
          />
        )}

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {/* Monatliches Abo */}
          {prices.monthly ? (
            <div className="bg-surface-raised shadow-lg rounded-xl border border-hairline p-6 hover:border-indigo-500 transition">
              <h2 className="text-xl font-semibold mb-2 text-primary">{prices.monthly.productName || 'Premium Monatlich'}</h2>
              <p className="mb-4 text-secondary">{prices.monthly.productDescription || 'Flexibles Monatsabo.'}</p>
              <p className="text-xl font-bold text-accent-ink mb-6">
                {formatPrice(prices.monthly.unitAmount, prices.monthly.currency)} / Monat
              </p>
              <button
                onClick={() => {
                  console.log("CLIENT: Button Click - Monthly - priceId to be used:", prices.monthly?.id);
                  handleCheckout(prices.monthly?.id, setLoadingCheckoutMonthly);
                }}
                disabled={loadingCheckoutMonthly || loadingCheckoutYearly || !prices.monthly?.id}
                className={`w-full px-4 py-3 rounded-md font-semibold ${
                  loadingCheckoutMonthly ? 'bg-surface-hover text-primary' : 'bg-indigo-600 hover:bg-indigo-700 text-[#0C1322]'
                } disabled:opacity-50 disabled:cursor-not-allowed transition`}
              >
                {loadingCheckoutMonthly ? 'Wird geladen...' : 'Jetzt monatlich abonnieren'}
              </button>
            </div>
          ) : (
            <div className="bg-surface-raised shadow-lg rounded-xl border border-hairline p-6">
               <h2 className="text-xl font-semibold mb-2 text-primary">Premium Monatlich</h2>
               <p className="text-muted">Details für dieses Paket konnten nicht geladen werden.</p>
            </div>
          )}

          {/* Jährliches Abo */}
          {prices.yearly ? (
            <div className="bg-surface-raised shadow-lg rounded-xl border border-hairline p-6 hover:border-indigo-500 transition">
              <div className="bg-indigo-600 text-[#0C1322] text-sm font-medium px-3 py-1 rounded-full w-fit mb-3">Empfohlen</div>
              <h2 className="text-xl font-semibold mb-2 text-primary">{prices.yearly.productName || 'Premium Jährlich'}</h2>
              <p className="mb-4 text-secondary">{prices.yearly.productDescription || 'Spare mit dem Jahresabo.'}</p>
              <p className="text-xl font-bold text-accent-ink mb-6">
                {formatPrice(prices.yearly.unitAmount, prices.yearly.currency)}
                {prices.yearly.recurring?.interval === 'year' ? ' / Jahr' : ' / Monat'}
                {prices.yearly.unitAmount && prices.yearly.recurring?.interval === 'year' && (
                  <span className="text-sm font-normal text-muted block">
                    (entspricht {formatPrice(prices.yearly.unitAmount / 12, prices.yearly.currency)} / Monat bei jährlicher Zahlung)
                  </span>
                )}
                 {prices.yearly.unitAmount && prices.yearly.recurring?.interval === 'month' && prices.yearly.id === 'price_1RD2HhRhesKAfWh3bNIxiB7n' && (
                   // Annahme: Jährlicher Preis wird als Monatsbetrag x 12 oder ähnliches in Stripe konfiguriert.
                   // Wenn der Jahrespreis als ein Gesamtbetrag mit interval: year in Stripe ist, ist der obere Block korrekt.
                   // Dieser Block ist ein Fallback, falls der Jahresplan fälschlicherweise als Monatsintervall mit Jahres-ID kommt.
                  <span className="text-sm font-normal text-muted block">
                    (Jährliche Zahlung: {formatPrice(prices.yearly.unitAmount * 12, prices.yearly.currency)})
                  </span>
                )}
              </p>
              <button
                onClick={() => {
                  console.log("CLIENT: Button Click - Yearly - priceId to be used:", prices.yearly?.id);
                  handleCheckout(prices.yearly?.id, setLoadingCheckoutYearly);
                }}
                disabled={loadingCheckoutMonthly || loadingCheckoutYearly || !prices.yearly?.id}
                className={`w-full px-4 py-3 rounded-md font-semibold ${
                  loadingCheckoutYearly ? 'bg-surface-hover text-primary' : 'bg-indigo-600 hover:bg-indigo-700 text-[#0C1322]'
                } disabled:opacity-50 disabled:cursor-not-allowed transition`}
              >
                {loadingCheckoutYearly ? 'Wird geladen...' : 'Jetzt jährlich abonnieren'}
              </button>
              {/* Man könnte hier auch dynamisch einen Sparhinweis berechnen, falls Monats- und Jahrespreis vorhanden sind */}
               {prices.monthly?.unitAmount && prices.yearly?.unitAmount && prices.yearly.recurring?.interval === 'year' && (
                  (() => {
                      const yearlyTotal = prices.yearly.unitAmount!;
                      const monthlyAsYearly = prices.monthly.unitAmount! * 12;
                      if (yearlyTotal < monthlyAsYearly) {
                          const savingsPercentage = Math.round(((monthlyAsYearly - yearlyTotal) / monthlyAsYearly) * 100);
                          return <p className="mt-2 text-sm text-accent-ink">Spare {savingsPercentage}% im Vergleich zum Monatsabo</p>;
                      }
                      return null;
                  })()
              )}
            </div>
          ) : (
            <div className="bg-surface-raised shadow-lg rounded-xl border border-hairline p-6">
               <h2 className="text-xl font-semibold mb-2 text-primary">Premium Jährlich</h2>
               <p className="text-muted">Details für dieses Paket konnten nicht geladen werden.</p>
            </div>
          )}
        </div>
      </div>
    );
  }

  // Flag on: 3-Spalten-Vergleichstabelle Free/Premium/Ultra (M-UX-G3).
  return (
    <TierComparisonTable
      prices={prices}
      error={error}
      errorReason={checkoutErrorReason}
      onOpenPortal={handleOpenPortal}
      loadingCheckoutMonthly={loadingCheckoutMonthly}
      loadingCheckoutYearly={loadingCheckoutYearly}
      loadingCheckoutUltra={loadingCheckoutUltra}
      handleCheckout={handleCheckout}
      setLoadingCheckoutMonthly={setLoadingCheckoutMonthly}
      setLoadingCheckoutYearly={setLoadingCheckoutYearly}
      setLoadingCheckoutUltra={setLoadingCheckoutUltra}
      formatPrice={formatPrice}
    />
  );
};

export default SubscriptionPage;
